import { useState } from 'react';
import {
  TECH_EXCLUSION_REASONS, memberSearch, techExclusionAdd, techExclusionPreview,
  type TechExclusionPreviewResp, type TechExclusionReason, type TechExclusionResp, type TechExclusionRowOutcome,
} from '@lib/care-api';
import { TECH_EXCLUSION_WORDING as W, dncRefusalWords, fmtPhone, type MemberCandidate } from '@lib/care-model';
import { fmtMemberId } from '@lib/member-id';

/**
 * ROW 372 (William, 18 Sep 2026), on tab 13 only: "a lookup for members that marny can ADD to the
 * lockout list. For example, if someone has died or gone into hospice, then there is a way to lookup
 * that person in Care and then a button to add to technical exclusion (which will stop all comms)".
 *
 * "BOTH ROWS" (his word of 19 Sep 2026, R. 82): one press writes the member-keyed hold AND a
 * phone-keyed block for every phone on the roster, shared phones included, because the platform's
 * send gate stops a text only when the PHONE is on the do-not-text list.
 *
 * FOUR STEPS, each one the operator can see before the next:
 *   1. FIND with the member search the case drawer already uses (name, member ID or phone).
 *   2. CHECK who it is: the preview reads the member BY ID and shows the name, the member ID, the
 *      YEAR of birth, and every phone with who else holds it.
 *   3. PICK a reason from his list (Other asks for words, kept in the audit only).
 *   4. CONFIRM: the panel lists BOTH rows the press writes, the member hold and each phone, and on a
 *      phone another member also holds it names that member and asks for a tick, "I understand the
 *      shared phone(s) are blocked too", without which nothing is sent. After the press the same
 *      rows show their outcome: enforced, blocked, already on the list, or refused and why.
 *
 * The addition sends the member ID, the reason and the tick, and nothing else: the server re-reads
 * the phones and the name from the roster and refuses a body carrying either. An answer that never
 * arrived keeps its request id for THIS member and THIS reason only, so a retry replays the first
 * request and a changed reason is never quietly sent under the old one's id.
 */
const MAX_WORDS = 300;
const OTHERS_SHOWN = 5;

/** "JANE DOE (00000123456), JOHN ROE (00000123457), and 3 more": the other holders of one phone. */
function othersWords(others: Array<{ memberId: string; fullName: string }>, otherCount: number): string {
  const named = others.slice(0, OTHERS_SHOWN).map((o) => `${o.fullName || 'No name'} (${fmtMemberId(o.memberId)})`);
  const more = otherCount - Math.min(others.length, OTHERS_SHOWN);
  return named.join(', ') + (more > 0 ? `, ${W.andMore(more)}` : '');
}

/** Every other member on any shared phone, once each, for the tick's own sentence. */
function sharedOthers(d: TechExclusionPreviewResp): Array<{ memberId: string; fullName: string }> {
  const seen = new Map<string, { memberId: string; fullName: string }>();
  for (const p of d.shared) for (const o of p.others) if (!seen.has(o.memberId)) seen.set(o.memberId, o);
  return [...seen.values()];
}

export function TechExclusionPanel({ platformWrite, onDone }: { platformWrite: boolean; onDone: () => void | Promise<void> }): JSX.Element {
  const [typed, setTyped] = useState('');
  const [candidates, setCandidates] = useState<MemberCandidate[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [chosen, setChosen] = useState<TechExclusionPreviewResp | null>(null);
  const [reasonCode, setReasonCode] = useState<TechExclusionReason | ''>('');
  const [words, setWords] = useState('');
  /** The confirm block is open (step 4), and whether the shared-phone line is ticked. */
  const [confirming, setConfirming] = useState(false);
  const [acked, setAcked] = useState(false);
  /** The last press's answer, shown row by row until the next member is chosen. */
  const [outcome, setOutcome] = useState<TechExclusionResp | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  /** An unanswered addition's id, and the member and reason it was sent for. */
  const [pending, setPending] = useState<{ key: string; id: string } | null>(null);

  const pendingKey = chosen && reasonCode ? `${chosen.memberId}|${reasonCode}` : '';
  const pendingId = pending && pending.key === pendingKey ? pending.id : '';

  async function search(): Promise<void> {
    const q = typed.trim();
    setErr('');
    setOk('');
    if (q.length < 2) { setErr(W.searchTooShort); return; }
    setSearching(true);
    try {
      const r = await memberSearch(q, 10);
      setCandidates(r.candidates ?? []);
    } catch (e) {
      setCandidates(null);
      setErr(W.searchFailed(e instanceof Error ? e.message : String(e)));
    } finally {
      setSearching(false);
    }
  }

  /** The preview, read by member id; on success it becomes the chosen member (fresh, un-ticked). */
  async function readPreview(memberId: string): Promise<boolean> {
    const res = await techExclusionPreview(memberId);
    const d = res.data as { status?: unknown; error?: unknown };
    if (res.ok) {
      setChosen(res.data as TechExclusionPreviewResp);
      setAcked(false);
      return true;
    }
    if (res.status === 404) setErr(W.notOnRoster);
    else if (res.status === 409 && d.status === 'platform_write_off') setErr(W.platformOff);
    else if (res.status === 409 && d.status === 'capability-absent') setErr(W.notEnabled);
    else if (res.status === 503) setErr(W.rosterUnreadable);
    else setErr(W.previewFailed(String(d.error ?? `Request failed (${res.status})`)));
    return false;
  }

  async function choose(c: MemberCandidate): Promise<void> {
    setErr('');
    setOk('');
    setOutcome(null);
    setBusy(true);
    try {
      if (await readPreview(c.member_id)) {
        setReasonCode('');
        setWords('');
        setConfirming(false);
      }
    } catch (e) {
      setErr(W.previewFailed(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  function reset(): void {
    setChosen(null);
    setReasonCode('');
    setWords('');
    setConfirming(false);
    setAcked(false);
    setPending(null);
  }

  /** The answer in one sentence: what stands, what was refused and why. */
  function answerSentence(d: TechExclusionResp, name: string): { ok: boolean; text: string } {
    const refused = d.rows.filter((x) => x.status === 'refused');
    const why = [...new Set(refused.map((x) => dncRefusalWords(x.reason || 'refused')))].join('; ');
    const tail = !d.fileWritten && d.fileError ? W.fileNotWritten(d.fileError) : '';
    if (d.status === 'added') return { ok: true, text: W.added(name) + tail };
    if (d.status === 'partial') return { ok: false, text: W.partial(name, why) + tail };
    if (d.platformError && !d.replayable) return { ok: false, text: W.refusedOutright(d.platformError) };
    return { ok: false, text: W.refused(name, why || d.platformError || 'no reason given') };
  }

  /** Step 3 to 4: the reason is checked here, then the confirm block opens. Nothing is sent. */
  function openConfirm(): void {
    if (!chosen) return;
    setErr('');
    setOk('');
    if (!reasonCode) { setErr(W.reasonRequired); return; }
    const said = words.trim();
    if (reasonCode === 'other' && !said) { setErr(W.otherRequired); return; }
    if (said.length > MAX_WORDS) { setErr(W.wordsTooLong(MAX_WORDS)); return; }
    setAcked(false);
    setConfirming(true);
  }

  /** The press. Refused HERE without the tick when a phone is shared, before anything is sent. */
  async function add(): Promise<void> {
    if (!chosen || !reasonCode) return;
    setErr('');
    setOk('');
    if (chosen.ackRequired && !acked) { setErr(W.sharedAckRequired); return; }
    const said = words.trim();
    const name = chosen.fullName || fmtMemberId(chosen.memberId);
    setBusy(true);
    try {
      const res = await techExclusionAdd({
        memberId: chosen.memberId,
        reasonCode,
        ...(said ? { words: said } : {}),
        ...(chosen.ackRequired && acked ? { sharedPhoneAck: true } : {}),
        ...(pendingId ? { clientRequestId: pendingId } : {}),
      });
      if (!res.ok) {
        const e = res.data as { status?: unknown; error?: unknown };
        if (res.status === 409 && e.status === 'platform_write_off') setErr(W.platformOff);
        else if (res.status === 409 && e.status === 'capability-absent') setErr(W.notEnabled);
        else if (res.status === 409 && e.status === 'shared_phone_ack_required') {
          // The roster changed since the preview: a phone is shared now. Show the server's sentence
          // and re-read the member, so the confirm names the new holder and asks for the tick.
          setErr(String(e.error ?? 'a shared phone needs the tick'));
          await readPreview(chosen.memberId);
        } else if (res.status === 404) setErr(W.notOnRoster);
        else setErr(String(e.error ?? `Request failed (${res.status})`));
        return;
      }
      const d = res.data as TechExclusionResp;
      if (d.status === 'unknown') {
        // Keep the id for this member and this reason: the next click replays, and writes nothing twice.
        setPending({ key: pendingKey, id: d.clientRequestId });
        setErr(W.unknown(d.platformError ?? 'no answer'));
        return;
      }
      setPending(null);
      setOutcome(d);
      const s = answerSentence(d, name);
      if (s.ok) { setOk(s.text); reset(); setCandidates(null); setTyped(''); }
      else setErr(s.text);
      await onDone();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const rowOutcomeLine = (x: TechExclusionRowOutcome): string => (x.memberKeyed
    ? W.memberRowOutcome(x.status, dncRefusalWords(x.reason || 'refused'))
    : W.phoneRowOutcome(fmtPhone(x.phone), x.status, dncRefusalWords(x.reason || 'refused')));

  return (
    <div className="note" style={{ marginBottom: 14, display: 'grid', gap: 8 }} data-testid="tech-exclusion-panel">
      <div><strong>{W.title}</strong></div>
      <div className="small muted">{W.lede}</div>

      {!platformWrite ? (
        <div className="small">{W.platformOff}</div>
      ) : (
        <>
          {!chosen && (
            <form
              onSubmit={(e) => { e.preventDefault(); void search(); }}
              style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}
            >
              <input
                className="text-input"
                aria-label={W.searchLabel}
                placeholder={W.searchPlaceholder}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                style={{ minWidth: 260 }}
              />
              <button type="submit" className="btn btn-sm" disabled={searching || busy}>{searching ? W.searching : W.searchButton}</button>
            </form>
          )}

          {!chosen && candidates !== null && (
            candidates.length === 0 ? (
              <div className="small muted">{W.noMatches}</div>
            ) : (
              <table className="dt dt-mini">
                <thead><tr><th>Member</th><th>{W.memberId}</th><th /></tr></thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.member_id}>
                      <td>{c.full_name || <span className="muted">No name</span>}</td>
                      <td>{fmtMemberId(c.member_id)}</td>
                      <td><button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => void choose(c)}>{W.choose}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )
          )}

          {chosen && (
            <div style={{ display: 'grid', gap: 6 }}>
              <div>
                <span className="muted">{W.chosenHeading}: </span><strong>{chosen.fullName || fmtMemberId(chosen.memberId)}</strong>
                <span className="muted"> &middot; {W.memberId} </span>{fmtMemberId(chosen.memberId)}
                <span className="muted"> &middot; {W.birthYear} </span>{chosen.birthYear || <span className="muted">{W.noBirthYear}</span>}
              </div>
              <div className="small">
                <div className="muted">{W.phonesHeading}</div>
                {chosen.phones.length === 0 && <div>{W.noPhones}</div>}
                {chosen.phones.map((p) => (
                  <div key={p.phone} style={{ marginTop: 2 }}>
                    <strong>{fmtPhone(p.phone)}</strong>{' '}
                    {p.otherCount === 0 ? W.phoneOnlyThis : W.sharedPhone(p.otherCount)}
                    {p.others.length > 0 && (
                      <div className="muted">{W.sharedWith}: {othersWords(p.others, p.otherCount)}</div>
                    )}
                  </div>
                ))}
              </div>

              {!confirming && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    className="text-input"
                    aria-label={W.reasonPick}
                    value={reasonCode}
                    onChange={(e) => setReasonCode(e.target.value as TechExclusionReason | '')}
                  >
                    <option value="">{W.reasonPlaceholder}</option>
                    {TECH_EXCLUSION_REASONS.map((k) => <option key={k} value={k}>{W.reasonLabel[k]}</option>)}
                  </select>
                  {reasonCode === 'other' && (
                    <input
                      className="text-input"
                      aria-label={W.otherPlaceholder}
                      placeholder={W.otherPlaceholder}
                      maxLength={MAX_WORDS}
                      value={words}
                      onChange={(e) => setWords(e.target.value)}
                      style={{ minWidth: 300 }}
                    />
                  )}
                  <button type="button" className="btn btn-sm btn-danger" disabled={busy} onClick={openConfirm}>
                    {pendingId ? W.retryButton : W.button}
                  </button>
                  <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={reset}>{W.cancel}</button>
                </div>
              )}

              {confirming && reasonCode && (
                <div className="note warn" style={{ display: 'grid', gap: 6 }} data-testid="tech-exclusion-confirm">
                  <div><strong>{W.confirmHeading(chosen.fullName || fmtMemberId(chosen.memberId), fmtMemberId(chosen.memberId), chosen.birthYear)}</strong></div>
                  <div>{W.confirmReason(W.reasonLabel[reasonCode] ?? reasonCode)}</div>
                  <div className="small">
                    <div className="muted">{W.confirmWrites}</div>
                    {/* BOTH ROWS: the member hold first, then one line per phone, shared ones naming who else. */}
                    <div>{W.memberRowLine(chosen.fullName || fmtMemberId(chosen.memberId))}</div>
                    {chosen.phones.map((p) => (
                      <div key={p.phone}>
                        {W.phoneRowLine(fmtPhone(p.phone))}
                        {p.otherCount > 0 && <> <strong>{W.alsoBlocks(othersWords(p.others, p.otherCount))}</strong></>}
                      </div>
                    ))}
                  </div>
                  {chosen.ackRequired && (
                    <label className="small" style={{ display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                      <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} data-testid="tech-exclusion-ack" />
                      <span>{W.sharedAck(othersWords(sharedOthers(chosen), chosen.sharedMemberIds.length))}</span>
                    </label>
                  )}
                  <div className="small muted">{W.confirmLocked}</div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="btn btn-sm btn-danger" disabled={busy || (chosen.ackRequired && !acked)} onClick={() => void add()}>
                      {pendingId ? W.retryButton : W.confirmButton}
                    </button>
                    <button type="button" className="btn btn-sm btn-ghost" disabled={busy} onClick={() => { setConfirming(false); setAcked(false); }}>{W.confirmBack}</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {outcome && (
        <div className="small" data-testid="tech-exclusion-outcome">
          <div className="muted">{W.outcomeHeading(outcome.fullName || fmtMemberId(outcome.memberId))}</div>
          {outcome.rows.map((x) => (
            <div key={x.memberKeyed ? 'member' : x.phone}>
              {rowOutcomeLine(x)}
              {!x.memberKeyed && x.otherCount > 0 && <> <strong>{W.alsoBlocks(othersWords(x.others, x.otherCount))}</strong></>}
            </div>
          ))}
        </div>
      )}
      {err && <div className="note danger">{err}</div>}
      {ok && <div className="note">{ok}</div>}
    </div>
  );
}
